import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, CreditCard } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import Header from "../../components/VibrantSolComponents/Header";
import Footer from "../../components/VibrantSolComponents/Footer";
import productimg from "../../product-01-330x440.png";
import productimg1 from "../../product-02-330x440.png";

function Checkout() {
  const navigate = useNavigate();

  // For demo, no actual submission logic. Extend for real API calls.
  const [formData, setFormData] = useState({});
  const [billingSame, setBillingSame] = useState(true);

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const placeOrder = (e) => {
    e.preventDefault();
    alert("Order placed successfully!");
    navigate("/order-confirmation"); // example navigation; create route accordingly
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <nav className="flex justify-center py-4 bg-gray-50">
        <ol className="flex items-center space-x-2 text-gray-700 text-sm">
          <li>
            <span className="text-gray-400 font-medium select-none cursor-default">
              Home
            </span>
          </li>
          <li>
            <span className="text-gray-300 font-medium select-none">——</span>
          </li>
          <li>
            <span className="font-medium">Checkout</span>
          </li>
        </ol>
      </nav>

      <main className="max-w-7xl w-full mx-auto py-12 px-4 flex-grow">
        <h1 className="text-3xl md:text-4xl text-center mb-8 secdory-font">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card>
            <CardContent className="!p-6">
              <form onSubmit={placeOrder} noValidate>
                {/* Shipping Information */}
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 secdory-font">
                  <Receipt size={24} />
                  Shipping Information
                </h2>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <Input
                    name="firstName"
                    placeholder="First Name"
                    onChange={handleChange}
                    required
                    className="border rounded-md px-4 py-2"
                  />
                  <Input
                    name="lastName"
                    placeholder="Last Name"
                    onChange={handleChange}
                    required
                    className="border rounded-md px-4 py-2"
                  />
                  <Input
                    name="streetAddress"
                    placeholder="Street Address"
                    onChange={handleChange}
                    required
                    className="border rounded-md px-4 py-2 col-span-2"
                  />
                  <Input
                    name="aptSuite"
                    placeholder="Apt/Suite"
                    onChange={handleChange}
                    className="border rounded-md px-4 py-2"
                  />
                  <Input
                    name="city"
                    placeholder="City"
                    onChange={handleChange}
                    required
                    className="border rounded-md px-4 py-2"
                  />
                  <Input
                    name="state"
                    placeholder="State"
                    onChange={handleChange}
                    required
                    className="border rounded-md px-4 py-2"
                  />
                  <Input
                    name="zip"
                    placeholder="Zip Code"
                    onChange={handleChange}
                    required
                    className="border rounded-md px-4 py-2"
                  />
                  <Select name="country" defaultValue="india">
                    <SelectTrigger className="border rounded-md">
                      <SelectValue placeholder="Select Country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viet-nam">Viet Nam</SelectItem>
                      <SelectItem value="india">India</SelectItem>
                      <SelectItem value="usa">USA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email"
                    onChange={handleChange}
                    required
                    className="border rounded-md px-4 py-2"
                  />
                  <Input
                    name="phone"
                    placeholder="Phone Number"
                    onChange={handleChange}
                    className="border rounded-md px-4 py-2"
                  />
                </div>

                <div className="mb-8">
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={billingSame}
                      onChange={() => setBillingSame(!billingSame)}
                      className="rounded"
                    />
                    Billing address is the same as shipping
                  </label>
                </div>

                {/* Payment Information */}
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 secdory-font">
                  <CreditCard size={24} />
                  Payment Information
                </h2>

                <div className="flex gap-4 mb-6">
                  <button
                    type="button"
                    className="flex items-center gap-2 px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 transition"
                  >
                    Stripe
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-2 px-4 py-2 rounded border border-blue-400 bg-blue-50 font-semibold"
                  >
                    <CreditCard size={20} />
                    Credit Card
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <Input
                    name="cardNumber"
                    placeholder="Credit Card Number"
                    onChange={handleChange}
                    required
                    className="border rounded-md px-4 py-2 col-span-2"
                  />
                  <Select name="expirationMonth" defaultValue="february">
                    <SelectTrigger className="border rounded-md">
                      <SelectValue placeholder="Expiration Month" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="january">January</SelectItem>
                      <SelectItem value="february">February</SelectItem>
                      <SelectItem value="march">March</SelectItem>
                    </SelectContent>
                  </Select>

                  <Input
                    name="expirationYear"
                    placeholder="Year"
                    onChange={handleChange}
                    required
                    className="border rounded-md px-4 py-2"
                  />
                  <Input
                    name="securityCode"
                    placeholder="Security Code"
                    onChange={handleChange}
                    required
                    className="border rounded-md px-4 py-2 col-span-2"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary text-white py-3 rounded-md font-semibold mb-4 hover:bg-gray-800 transition"
                >
                  Place Order
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Order Summary */}
          <div>
            <Card>
              <CardContent className="!p-6">
                <h3 className="text-2xl font-bold mb-6 secdory-font">
                  Order Summary
                </h3>

                {[
                  {
                    image: productimg,
                    name: "Natural Coconut Cleansing Oil",
                    details: "Size: Fullsize",
                    price: 29,
                  },
                  {
                    image: productimg1,
                    name: "Cleansing Balm",
                    details: "Color: Green – Revitalizing",
                    price: 29,
                  },
                ].map((product, idx) => (
                  <div key={idx} className="flex gap-4 mb-5 items-center">
                    <div className="custom-bg px-2 py-2 rounded-lg border border-gray-200">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="h-16 object-cover rounded-md"
                      />
                    </div>
                    <div>
                      <div className="font-semibold truncate max-w-xs">
                        {product.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {product.details}
                      </div>
                      <div className="mt-1 font-bold">
                        ${product.price.toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Totals */}
                <div className="border-t border-gray-200 my-6" />
                <div className="flex justify-between text-sm mb-2">
                  <span>Subtotal:</span>
                  <span>$58.00</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Shipping:</span>
                  <span>$0.00</span>
                </div>
                <div className="border-t border-gray-200 my-3" />
                <div className="flex justify-between font-bold text-lg mb-4">
                  <span>Total price:</span>
                  <span>$58.00</span>
                </div>

                <div className="mt-2 bg-pink-100 text-pink-800 px-4 py-3 rounded select-none">
                  You are eligible for{" "}
                  <span className="font-bold">free shipping!</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Checkout;
